/**
 * The four hooks a feature screen needs. Use these rather than calling
 * `useQuery` with a raw fetch — they carry the retry policy, the key convention
 * and the cursor handling.
 *
 *   useApiQuery       one resource or one arbitrary GET
 *   usePaginatedList  one page at a time, with next/previous (tables)
 *   useInfiniteList   accumulating pages (feeds, timelines, pickers)
 *   useApiMutation    any write, with invalidation declared up front
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryKey,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import type { RequestOptions } from '../client';
import { clampLimit, extractCursor, flattenPages } from '../pagination';
import type { CursorPage, ListParams } from '../types';
import { useApi } from './provider';

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export function useApiQuery<T>(
  key: QueryKey,
  path: string,
  options?: {
    query?: RequestOptions['query'];
    enabled?: boolean;
  } & Omit<UseQueryOptions<T, unknown, T, QueryKey>, 'queryKey' | 'queryFn' | 'enabled'>,
): UseQueryResult<T, unknown> {
  const api = useApi();
  const { query, enabled, ...rest } = options ?? {};
  return useQuery<T, unknown, T, QueryKey>({
    queryKey: key,
    queryFn: ({ signal }) => api.get<T>(path, { query, signal }),
    enabled: enabled ?? true,
    ...rest,
  });
}

/**
 * Page-at-a-time cursor pagination, matching `kernel/api/pagination.py`.
 *
 * The cursor is opaque and is held in component state, so "previous" works without
 * the client knowing anything about the ordering. There are no page numbers — a
 * cursor-paginated list genuinely does not have them, and a UI that shows "page 4"
 * over a cursor API is lying.
 */
export function usePaginatedList<T>(
  resourceKey: QueryKey,
  path: string,
  params: ListParams = {},
  options?: {
    enabled?: boolean;
    /** Poll the current page. React Query pauses polling while the tab is hidden, so an
     *  abandoned tab is not a permanent load on the API. */
    refetchInterval?: number | false;
  },
) {
  const api = useApi();
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const { limit, ...filters } = params;

  const query = useQuery<CursorPage<T>>({
    queryKey: [...(resourceKey as unknown[]), { ...filters, limit, cursor }] as QueryKey,
    queryFn: ({ signal }) =>
      api.get<CursorPage<T>>(path, {
        query: { ...filters, limit: clampLimit(limit as number | undefined), cursor },
        signal,
      }),
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval ?? false,
    placeholderData: (previous) => previous,
  });

  const nextCursor = extractCursor(query.data?.next);
  const prevCursor = extractCursor(query.data?.previous);

  return {
    ...query,
    items: query.data?.results ?? [],
    hasNext: Boolean(query.data?.next),
    hasPrevious: Boolean(query.data?.previous),
    goNext: useCallback(() => nextCursor && setCursor(nextCursor), [nextCursor]),
    goPrevious: useCallback(() => setCursor(prevCursor), [prevCursor]),
    /** Call after changing a filter, or the new filter is applied at an old position. */
    resetCursor: useCallback(() => setCursor(undefined), []),
    isFirstPage: cursor === undefined,
  };
}

/** Accumulating pages. Use for timelines, activity feeds and long pickers. */
export function useInfiniteList<T>(
  resourceKey: QueryKey,
  path: string,
  params: ListParams = {},
  options?: { enabled?: boolean },
) {
  const api = useApi();
  const { limit, ...filters } = params;

  const query = useInfiniteQuery<
    CursorPage<T>,
    unknown,
    InfiniteData<CursorPage<T>, string | undefined>,
    QueryKey,
    string | undefined
  >({
    queryKey: [...(resourceKey as unknown[]), { ...filters, limit }] as QueryKey,
    initialPageParam: undefined,
    queryFn: ({ pageParam, signal }) =>
      api.get<CursorPage<T>>(path, {
        query: { ...filters, limit: clampLimit(limit as number | undefined), cursor: pageParam },
        signal,
      }),
    getNextPageParam: (lastPage) => extractCursor(lastPage.next),
    getPreviousPageParam: (firstPage) => extractCursor(firstPage.previous),
    enabled: options?.enabled ?? true,
  });

  return {
    ...query,
    /** Every loaded page, flattened. */
    items: flattenPages<T>(query.data?.pages),
  };
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

export interface ApiMutationConfig<TData, TVariables> {
  /** Query keys to invalidate on success. Prefer the broad root, e.g. `['cases']`. */
  invalidates?: QueryKey[];
  mutationFn: (variables: TVariables) => Promise<TData>;
}

/**
 * A write with its invalidation declared next to it. Nothing is retried — see
 * `query/client.ts` for why.
 */
export function useApiMutation<TData, TVariables>(
  config: ApiMutationConfig<TData, TVariables>,
  options?: Omit<UseMutationOptions<TData, unknown, TVariables>, 'mutationFn'>,
): UseMutationResult<TData, unknown, TVariables> {
  const queryClient = useQueryClient();
  return useMutation<TData, unknown, TVariables>({
    mutationFn: config.mutationFn,
    ...options,
    // Rest args rather than a fixed signature: TanStack has changed the arity of
    // this callback between minor versions, and forwarding whatever it was given
    // keeps this hook working across them.
    onSuccess: async (
      ...args: Parameters<
        NonNullable<UseMutationOptions<TData, unknown, TVariables>['onSuccess']>
      >
    ) => {
      await Promise.all(
        (config.invalidates ?? []).map((key) => queryClient.invalidateQueries({ queryKey: key })),
      );
      await options?.onSuccess?.(...args);
    },
  });
}
